import { Autocomplete } from '@Components/Autocomplete/Autocomplete';
import EdNumericInput from '@Components/UI/Inputs/EdNumericInput/EdNumericInput';
import { useTypedSelector } from '@Features/store';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Modal, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import {
	CheckboxStyled,
	Container,
	ButtonsContainer,
	TitleStyled,
} from '@Pages/ScratchCards/components/GenerateCode';
import { emptyToNull } from '@Pages/ScratchCards/components/TargetModePicker';
import { useSnackbar } from '@Providers/useSnackbar';
import { Spacer } from '@Styled/utilities';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { instructorCodesGenerate } from '../Slices/InstructorCodes.slice';

type Props = {
	handleModelClose: () => void;
	openModel: boolean;
};

// Mirrors the backend cap (rules.range(1, 10000)): the generator builds the
// whole batch in memory before inserting.
const QUANTITY_MAX = 10000;

// Mirrors the backend rule: codes are 8 characters, digits from 1-9, with an
// optional 1-2 capital-letter lead counted inside the 8 (FM -> FM123456).
// I, L and O are excluded for the same legibility reason 0 is.
const PREFIX_REGEX = /^[A-HJKMNP-Z]{1,2}$/;

const SCHEMA = yup.object().shape({
	instructor_id: yup
		.number()
		.label('Instructor')
		.transform(emptyToNull)
		.nullable()
		.required(),
	quantity: yup
		.number()
		.label('Quantity')
		.transform(emptyToNull)
		.nullable()
		.typeError('Quantity must be a number')
		.positive()
		.integer()
		.max(QUANTITY_MAX)
		.required(),
	code_prefix: yup
		.string()
		.label('Code prefix')
		.transform((value) => (value === '' ? undefined : value))
		.matches(PREFIX_REGEX, {
			message: 'Prefix must be 1-2 capital letters (no I, L or O)',
			excludeEmptyString: true,
		})
		.notRequired(),
	group_name: yup.string().label('Group name').notRequired(),
	checker: yup.boolean(),
});

/**
 * Generates unlock-center-classroom codes for one instructor. Much simpler
 * than the scratchcard modal: there is no target to pick — the instructor IS
 * the target — and the format is fixed numeric.
 */
const GenerateInstructorCodes: React.FC<Props> = ({
	handleModelClose,
	openModel,
}) => {
	const dispatch = useDispatch();
	const {
		formState: { isValid, isSubmitting },
		watch,
		reset,
		control,
		handleSubmit,
	} = useForm<any>({
		resolver: yupResolver(SCHEMA),
		mode: 'onTouched',
		defaultValues: {
			instructor_id: null,
			quantity: null,
			code_prefix: '',
			group_name: '',
			checker: false,
		},
	});

	const { dropdownInstructors } = useTypedSelector((state) => state.dropdowns);
	const { displaySnackbar } = useSnackbar();

	const onSubmitForm = async (formData: Record<string, any>) => {
		const payload: Record<string, any> = {
			instructor_id: formData.instructor_id,
			quantity: formData.quantity,
		};
		if (formData.code_prefix) payload.code_prefix = formData.code_prefix;
		if (formData.group_name) payload.group_name = formData.group_name;

		try {
			await (
				dispatch(instructorCodesGenerate(payload as any)) as any
			).unwrap();
			closeAndReset();
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ||
					error?.message ||
					'Unable to generate codes'
			);
		}
	};

	const closeAndReset = () => {
		reset();
		handleModelClose();
	};

	return (
		<Modal
			open={openModel}
			onClose={closeAndReset}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Container>
				<form onSubmit={handleSubmit(onSubmitForm)}>
					<Box sx={{ marginLeft: '1.5rem', marginTop: '2.25rem' }}>
						<TitleStyled id="modal-modal-title">
							Generate Unlock Center Codes
						</TitleStyled>
					</Box>

					<Box
						sx={{
							marginLeft: '3.5rem',
							marginRight: '4rem',
							marginTop: '1.5rem',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Controller
							control={control}
							name="instructor_id"
							render={({ field, fieldState }) => (
								<FormControl
									sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
									size="small"
								>
									<Autocomplete
										label="Select Instructor"
										options={dropdownInstructors.map((_) => ({
											id: _.id,
											label: `${_.first_name} ${_.last_name}`,
										}))}
										size="small"
										{...field}
										fieldState={fieldState}
										textFieldProps={{ sx: { width: '100%' } }}
									/>
								</FormControl>
							)}
						/>
						<Controller
							control={control}
							name="quantity"
							render={({ field, fieldState }) => (
								<Spacer my="0.5rem">
									<EdNumericInput
										fullWidth
										size="small"
										label="Set Quantity"
										placeholder="Quantity"
										error={fieldState.invalid}
										helperText={fieldState.error?.message}
										{...field}
										variant="outlined"
									/>
								</Spacer>
							)}
						/>
						<Controller
							control={control}
							name="code_prefix"
							render={({ field, fieldState }) => (
								<Spacer my="0.5rem">
									<TextField
										fullWidth
										size="small"
										label="Code prefix (optional)"
										placeholder="e.g. FM"
										error={fieldState.invalid}
										helperText={
											fieldState.error?.message ??
											'Up to 2 capital letters (no I, L or O). FM makes codes like FM123456; empty makes 8 digits.'
										}
										{...field}
										onChange={(e) =>
											// Typed lowercase is meant as the letter — uppercase it
											// instead of failing validation on a technicality.
											field.onChange(e.target.value.toUpperCase())
										}
										inputProps={{ maxLength: 2 }}
										variant="outlined"
									/>
								</Spacer>
							)}
						/>
						<Controller
							control={control}
							name="group_name"
							render={({ field, fieldState }) => (
								<Spacer my="0.5rem">
									<TextField
										fullWidth
										size="small"
										label="Group name (optional)"
										placeholder="Group name"
										error={fieldState.invalid}
										helperText={
											fieldState.error?.message ??
											'A label stamped on every code in this batch, e.g. the class group these will be handed to.'
										}
										{...field}
										variant="outlined"
									/>
								</Spacer>
							)}
						/>
						<Controller
							control={control}
							name="checker"
							render={({ field }) => (
								<CheckboxStyled
									control={<Checkbox />}
									{...field}
									checked={!!field.value}
									label="All selected options are correct"
								/>
							)}
						/>
					</Box>
					<ButtonsContainer>
						<Button
							variant="text"
							color="inherit"
							sx={{
								fontFamily: 'Roboto',
								fontSize: '14px',
								padding: '6px 16px',
								color: '#6c63ff',
								backgroundColor: '#fff',
								outline: 'none',
							}}
							// Codes cannot be un-generated: valid fields + explicit tick, and
							// locked while in flight — every extra click is a new batch.
							disabled={!isValid || !watch('checker') || isSubmitting}
							type="submit"
						>
							Submit
						</Button>
						<Button
							variant="text"
							color="warning"
							sx={{
								fontFamily: 'Roboto',
								fontSize: '14px',
								padding: '6px 16px',
								color: '#ff9800',
								outline: 'none',
								backgroundColor: '#fff',
							}}
							onClick={closeAndReset}
						>
							Cancel
						</Button>
					</ButtonsContainer>
				</form>
			</Container>
		</Modal>
	);
};

export default GenerateInstructorCodes;
